import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { ApiService } from '../../lib/api.service';
import { LogService } from '../../lib/log.service';
import { ModalService } from '../../lib/modal.service';
import { Organization } from '../../models/organization';
import { User } from '../../models/user';
import { UserService } from '../../lib/user.service';
import { TranslateService } from '@ngx-translate/core';
import { config } from '../../../environments/all';
import { MultiAutoItem } from '../../models/multiautoitem';

@Component({
    selector: 'app-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.scss'],
    standalone: false,
})
export class UserEditComponent implements OnInit {
    userEditForm: FormGroup;
    organizationDataForm: FormGroup;
    isLoading = true;
    userId: number;
    user: User;
    currentLoggedInUser: User;
    organizations: Organization[];
    multiSelOrgs: Array<MultiAutoItem> = [];
    organizationIds = [];
    roles = [
        { id: 1, name: 'User' },
        { id: 2, name: 'Professional' },
        { id: 3, name: 'Executive' },
        { id: 4, name: 'Executive Professional' },
        { id: 5, name: 'Joint Professional' },
        { id: 6, name: 'Relationship Manager' },
        { id: 7, name: 'Admin' },
    ];
    timezones: any;
    passwordType: string = 'password';
    isRelationshipManager: boolean = false;

    constructor(
        private formBuilder: FormBuilder,
        private api: ApiService,
        private log: LogService,
        private modalService: ModalService,
        private translate: TranslateService,
        private userService: UserService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private location: Location
    ) {
        this.currentLoggedInUser = this.userService.getUser();
        this.timezones = config.timezones;
    }

    ngOnInit() {
        this.userId = parseInt(this.activatedRoute.snapshot.paramMap.get('id'), 10);
        this.initForms();
        this.loadOrganizations();
        this.loadUser();
    }

    initForms() {
        this.userEditForm = this.formBuilder.group({
            fullName: ['', [Validators.required]],
            email: ['', [Validators.required, Validators.pattern(/^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,4},?)+$/)]],
            status: ['approved'],
            notificationOn: [true],
            timezone: [''],
            roleId: [1],
            lockedRole: [false],
            password: ['', [Validators.minLength(8), Validators.maxLength(32)]],
            orgId: [null],
        });

        this.organizationDataForm = this.formBuilder.group({
            orgId: [[]],
        });
    }

    loadOrganizations() {
        this.api.get('admin/organizations').subscribe(
            (results: any) => {
                this.organizations = Organization.initializeArray(results.data);
                this.multiSelOrgs = [];
                this.organizations.forEach(org => {
                    this.multiSelOrgs.push(new MultiAutoItem(org.id, org.name));
                });
            },
            (error: any) => {
                this.log.error('Error loading organizations. ' + error.message);
            }
        );
    }

    loadUser() {
        this.api.get('users/' + this.userId, {}).subscribe(
            (result: any) => {
                this.user = new User(result.data);
                this.isRelationshipManager = this.user.userType === 'relationshipmanager';

                this.userEditForm.patchValue({
                    fullName: this.user.fullName,
                    email: this.user.email,
                    status: this.user.status,
                    notificationOn: this.user.preferences?.notificationOn,
                    timezone: this.user.preferences?.timezone,
                    roleId: this.user.roleId,
                    lockedRole: this.user.lockedRole,
                    orgId: this.user.roleId !== 6
                        ? this.user.organizations?.map(o => o.id)[0]
                        : this.user.organizations?.map(o => o.id),
                });

                this.organizationDataForm.patchValue({
                    orgId: this.user.organizations?.map(o => o.id),
                });

                // Remove password requirement for edit
                this.userEditForm.get('password').setValidators([]);
                this.userEditForm.get('password').updateValueAndValidity();

                this.isLoading = false;
            },
            (error: any) => {
                this.log.error('Error fetching user. ' + error.message);
                this.isLoading = false;
            }
        );
    }

    get f() {
        return this.userEditForm.controls;
    }

    onSave() {
        if (this.userEditForm.invalid) {
            return;
        }

        // Safety check: ensure we're editing the correct user from route params,
        // not the logged-in user
        if (this.user.id !== this.userId) {
            this.modalService.showAlert('Error', 'User ID mismatch. Please reload the page.');
            return;
        }

        const roleId = this.userEditForm.value.roleId;
        const payload: any = {
            Name: this.userEditForm.value.fullName,
            Email: this.userEditForm.value.email,
            RoleID: roleId,
            Status: this.userEditForm.value.status,
            OrgID: roleId === 6
                ? this.organizationDataForm.value.orgId
                : this.userEditForm.value.orgId,
            NotificationOn: this.userEditForm.value.notificationOn,
            lockedRole: this.userEditForm.value.lockedRole,
            Timezone: this.userEditForm.value.timezone,
        };

        // Only include password if the admin entered one
        const password = this.userEditForm.value.password;
        if (password) {
            payload.Password = password;
        }

        this.api
            .put('admin/usersave/' + this.userId, payload)
            .subscribe(
                (data: any) => {
                    this.modalService.showAlert('Success', 'User was successfully updated');
                    this.router.navigate(['/admin/user', this.userId]);
                },
                (error: any) => {
                    this.modalService.showAlert('Error', error.message);
                }
            );
    }

    onCancel() {
        this.location.back();
    }

    onRoleChange() {
        const roleId = this.userEditForm.value.roleId;
        if (roleId === 6) {
            this.userEditForm.get('orgId').clearValidators();
            this.userEditForm.get('orgId').updateValueAndValidity();
            this.organizationDataForm.get('orgId').setValidators([Validators.required]);
            this.organizationDataForm.get('orgId').updateValueAndValidity();
        } else if (roleId === 7) {
            this.userEditForm.get('orgId').clearValidators();
            this.userEditForm.get('orgId').updateValueAndValidity();
            this.organizationDataForm.get('orgId').clearValidators();
            this.organizationDataForm.get('orgId').updateValueAndValidity();
        } else {
            this.userEditForm.get('orgId').setValidators([Validators.required]);
            this.userEditForm.get('orgId').updateValueAndValidity();
            this.organizationDataForm.get('orgId').clearValidators();
            this.organizationDataForm.get('orgId').updateValueAndValidity();
        }
    }

    onSelectOrgs(items: MultiAutoItem[]) {
        this.organizationIds = items.map(item => item.id);
        this.organizationDataForm.patchValue({
            orgId: this.organizationIds,
        });
    }

    onClickPassword() {
        this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
    }
}
